/** Types for the label template editor */

export type ElementType = 'text' | 'datamatrix' | 'datamatrix_ka' | 'gs1_128' | 'ean13' | 'line' | 'rect'

export type WrapMode = 'wrap_words' | 'no_wrap' | 'break_all'
export type OverflowMode = 'visible' | 'clip'

export const FONT_FAMILIES = [
  { id: 'dejavu_sans', label: 'DejaVu Sans', css: '"TM-DejaVuSans", Arial, Helvetica, sans-serif' },
  { id: 'dejavu_sans_bold', label: 'DejaVu Sans Bold (толстый)', css: '"TM-DejaVuSansBold", Arial, Helvetica, sans-serif' },
  { id: 'dejavu_sans_mono', label: 'DejaVu Sans Mono', css: '"TM-DejaVuSansMono", "Courier New", monospace' },
  { id: 'liberation_sans', label: 'Liberation Sans', css: '"TM-LiberationSans", Arial, Helvetica, sans-serif' },
  { id: 'liberation_serif', label: 'Liberation Serif', css: '"TM-LiberationSerif", "Times New Roman", serif' },
  { id: 'dejavu_sans_condensed', label: 'DejaVu Sans Condensed', css: '"TM-DejaVuSansCondensed", "Arial Narrow", Arial, sans-serif' },
  { id: 'dejavu_sans_condensed_bold', label: 'DejaVu Sans Condensed Bold (толстый низкий)', css: '"TM-DejaVuSansCondensedBold", "Arial Narrow", Arial, sans-serif' },
] as const

export type FontFamilyId = (typeof FONT_FAMILIES)[number]['id']

export interface LabelElement {
  id: string
  type: ElementType
  x: number
  y: number
  width: number
  height: number
  rotation?: number
  // text-specific
  field?: string
  label?: string
  fontSize?: number
  fontWeight?: string
  fontFamily?: FontFamilyId | string
  align?: string
  wrapMode?: WrapMode | string
  overflowX?: OverflowMode | string
  overflowY?: OverflowMode | string
}

export interface TemplateData {
  name: string
  type: string
  width: number
  height: number
  elements: LabelElement[]
  for_km?: boolean
  for_ka_box?: boolean
  for_ka_pallet?: boolean
  /** default = из Настроек; mono/gray/rgba = приоритет этого шаблона */
  render_mode?: 'default' | 'mono' | 'gray' | 'rgba'
}

export const ELEMENT_TYPE_NAMES: Record<ElementType, string> = {
  text: 'Текст',
  datamatrix: 'GS1-DataMatrix',
  datamatrix_ka: 'GS1-DataMatrix (КА)',
  gs1_128: 'GS1-128',
  ean13: 'EAN-13',
  line: 'Линия',
  rect: 'Рамка',
}

export const ELEMENT_DEFAULTS: Record<ElementType, Partial<LabelElement>> = {
  text: {
    width: 50,
    height: 10,
    fontSize: 10,
    rotation: 0,
    fontFamily: 'dejavu_sans',
    wrapMode: 'wrap_words',
    overflowX: 'visible',
    overflowY: 'visible',
  },
  datamatrix: { width: 20, height: 20, field: 'km_code', rotation: 0 },
  datamatrix_ka: { width: 30, height: 30, field: 'ka_code', rotation: 0 },
  gs1_128: { width: 60, height: 30, field: 'ka_code', rotation: 0 },
  ean13: { width: 40, height: 20, field: 'gtin', rotation: 0 },
  line: { width: 50, height: 0.5, rotation: 0 },
  rect: { width: 50, height: 30, rotation: 0 },
}

export const DATA_FIELDS = [
  'km_code',
  'gtin',
  'production_date',
  'expiry_date',
  'ka_code',
  'sequence_number',
  'task_name',
  'task_comment',
  'task_extra_attribute',
  'task_id',
  'product_name',
  'product_code',
  'box_num',
  'pallet_num',
  'sequence_range',
  'box_range',
  'codes_count',
  'boxes_count',
  'km_in_box',
  'km_on_pallet',
] as const

export const TOOLBOX_ITEMS: { type: ElementType; label: string }[] = [
  { type: 'text', label: 'Текст' },
  { type: 'datamatrix', label: 'GS1-DataMatrix' },
  { type: 'gs1_128', label: 'GS1-128' },
  { type: 'ean13', label: 'EAN-13' },
]

export interface PreviewTestData {
  product_name: string
  product_code: string
  gtin: string
  km_code: string
  ka_code: string
  task_id: string
  task_name: string
  task_comment: string
  task_extra_attribute: string
  production_date: string
  expiry_date: string
  box_num: number
  pallet_num: number
  codes_count: number
  boxes_count: number
  sequence_number: number
  first_sequence: number
  last_sequence: number
  sequence_range: string
  first_box: number
  last_box: number
  box_range: string
  km_in_box: number
  km_on_pallet: number
}

export const DEFAULT_TEST_DATA: PreviewTestData = {
  product_name: 'Тестовый товар',
  product_code: 'ABC123',
  gtin: '04810285016605',
  km_code: '010481028501660521ABC1234567890',
  ka_code: '(01)04810285016605(37)10',
  task_id: '000210',
  task_name: 'Задание #2025-001',
  task_comment: 'Срочная партия',
  task_extra_attribute: 'Партия А-17',
  production_date: '15.01.2025',
  expiry_date: '15.01.2026',
  box_num: 1,
  pallet_num: 1,
  codes_count: 50,
  boxes_count: 5,
  sequence_number: 1,
  first_sequence: 1,
  last_sequence: 50,
  sequence_range: '1-50',
  first_box: 1,
  last_box: 5,
  box_range: '1-5',
  km_in_box: 24,
  km_on_pallet: 480,
}

export function cssFontFamily(id?: string): string {
  const found = FONT_FAMILIES.find(f => f.id === id)
  return found?.css ?? 'Arial, Helvetica, sans-serif'
}
